import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileDown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { trackEvent } from "../utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEB3FORMS_KEY = "13a079f4-4a52-433b-a223-9e1818f4593a";
const ELIGIBLE = ["/", "/kisan-kota-plots", "/kisan-kota-plots/", "/yeida-authority-plots", "/yeida-authority-plots/", "/listings", "/listings/"];
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const ExitIntentPopup = () => {
    const [show, setShow] = useState(false);
    const [form, setForm] = useState({ name: "", phone: "" });
    const [loading, setLoading] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        if (!ELIGIBLE.includes(pathname)) return undefined;
        const seen = localStorage.getItem("om_exit_intent_ts");
        if (seen && Date.now() - Number(seen) < THIRTY_DAYS) return undefined;

        let armed = false;
        const armTimer = setTimeout(() => { armed = true; }, 8000);
        let maxScroll = 0;

        const fire = () => {
            if (!armed) return;
            localStorage.setItem("om_exit_intent_ts", String(Date.now()));
            setShow(true);
            trackEvent("exit_intent_shown", { button_location: "exit-popup" });
            cleanup();
        };
        const onMouseOut = (e) => {
            if (!e.relatedTarget && e.clientY <= 0) fire();
        };
        const onScroll = () => {
            const pct = (window.scrollY / Math.max(1, document.body.scrollHeight - window.innerHeight)) * 100;
            if (pct > maxScroll) maxScroll = pct;
            if (maxScroll > 35 && pct < maxScroll - 12) fire(); // mobile scroll-up after depth
        };
        const cleanup = () => {
            document.removeEventListener("mouseout", onMouseOut);
            window.removeEventListener("scroll", onScroll);
            clearTimeout(armTimer);
        };
        document.addEventListener("mouseout", onMouseOut);
        window.addEventListener("scroll", onScroll, { passive: true });
        return cleanup;
    }, [pathname]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/leads`, {
                name: form.name,
                phone: form.phone,
                email: "",
                plot_size: "",
                sector: "",
                message: "Requested Free YEIDA Investor's Guide (19-page PDF) via exit popup",
                source: "Exit Intent Popup",
                page_url: window.location.href,
            });
            fetch("https://api.web3forms.com/submit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    access_key: WEB3FORMS_KEY,
                    subject: "Exit Popup Lead — Investor's Guide",
                    name: form.name,
                    phone: form.phone,
                    page: window.location.href,
                }),
            }).catch(() => {});
            toast.success("Guide on its way! Our team will WhatsApp you the PDF shortly.");
            setShow(false);
        } catch {
            toast.error("Could not submit. Please WhatsApp us instead.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[80] bg-navy/85 backdrop-blur-sm flex items-center justify-center p-4"
                    data-testid="exit-intent-popup"
                >
                    <motion.div
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        className="bg-white rounded-[14px] max-w-md w-full p-8 relative shadow-2xl"
                    >
                        <button onClick={() => setShow(false)} aria-label="Close popup" data-testid="exit-popup-close" className="absolute top-4 right-4 text-slate-400 hover:text-navy transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                        <p className="text-[0.78rem] font-semibold uppercase tracking-[1.4px] text-gold-deep">Before You Go</p>
                        <h2 className="mt-2 font-serif text-navy text-2xl font-bold">Free YEIDA Investor's Guide — 19-page PDF</h2>
                        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                            Sector rates, RPS scheme history, registry costs and appreciation data — the exact checklist we use to verify every plot.
                        </p>
                        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="exit-popup-form">
                            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="exit-popup-name" className="w-full border border-slate-300 rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-gold" />
                            <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone / WhatsApp number" data-testid="exit-popup-phone" className="w-full border border-slate-300 rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-gold" />
                            <button type="submit" disabled={loading} data-testid="exit-popup-submit" className="w-full bg-gold text-navy font-semibold text-sm px-5 py-3.5 rounded-[9px] hover:brightness-105 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50">
                                <FileDown className="w-4 h-4" /> {loading ? "Sending..." : "Send Me the Free Guide"}
                            </button>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ExitIntentPopup;
